import { Injectable, inject } from '@angular/core';
import {
    AllergyIntolerance,
    Bundle,
    BundleEntry,
    CodeableConcept,
    Coding,
    Composition,
    Condition,
    DiagnosticReport,
    DocumentReference,
    Encounter,
    Immunization,
    MedicationRequest,
    Observation,
    Organization,
    Patient,
    Reference,
    Resource,
    ServiceRequest
} from 'fhir/r4';
import { terminologyCanonicalUrlBaseToken } from '../../app.config';
import { ReferralDocumentArtifacts, ReferralWorkflowInput } from './referral-workflow.models';

interface GroupedSnapshotResources {
    allergies: AllergyIntolerance[];
    vitals: Observation[];
    physicalExam: Observation[];
    patientSymptoms: Observation[];
    referralNotes?: string;
    labRequests: ServiceRequest[];
    otherServiceRequests: ServiceRequest[];
    diagnosticReports: DiagnosticReport[];
    medicationRequests: MedicationRequest[];
    diagnoses: Condition[];
    pastDiagnoses: Condition[];
    immunizations: Immunization[];
    additionalResources: Resource[];
}

@Injectable({ providedIn: 'root' })
export class ReferralDocumentPayloadService {
    private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private readonly referralWorkflowSystem = `${this.terminologyBaseUrl}/CodeSystem/referral-workflow`;
    private readonly referralIdentifierSystem = `${this.terminologyBaseUrl}/NamingSystem/referral-identifier`;

    buildArtifacts(input: ReferralWorkflowInput): ReferralDocumentArtifacts {
        const patientId = input.source.patient?.id?.trim();
        if (!patientId) {
            throw new Error('Referral document generation requires an existing Patient.id.');
        }

        const referralIdentifier = input.referralIdentifier?.trim() || this.createIdentifierValue();
        const authoredOn = input.authoredOn?.trim() || new Date().toISOString();
        const title = input.title?.trim() || 'Referral Document';

        const groupedResources = this.groupResources(input);
        const composition = this.buildComposition(input, groupedResources, referralIdentifier, authoredOn, title);
        const documentBundle = this.buildDocumentBundle(input, composition, groupedResources, referralIdentifier, authoredOn);
        const documentReference = this.buildDocumentReference(
            input,
            documentBundle,
            referralIdentifier,
            authoredOn,
            title
        );

        return {
            referralIdentifier,
            authoredOn,
            composition,
            documentBundle,
            documentReference
        };
    }

    private buildComposition(
        input: ReferralWorkflowInput,
        grouped: GroupedSnapshotResources,
        referralIdentifier: string,
        authoredOn: string,
        title: string
    ): Composition {
        const patient = input.source.patient;
        const encounter = input.source.encounter;
        const author = this.buildAuthorReference(input);
        const custodian = this.buildOrganizationReference(input.sourceOrganizationId, input.sourceOrganizationDisplay);
        const sectionEntries = this.buildSections(grouped);

        return {
            resourceType: 'Composition',
            status: 'final',
            identifier: {
                system: this.referralIdentifierSystem,
                value: referralIdentifier
            },
            type: this.buildCodeableConcept('referral-document', 'Referral Document'),
            category: [this.buildCodeableConcept('referral', 'Referral')],
            title,
            date: authoredOn,
            subject: this.toReference(patient),
            encounter: this.toReference(encounter),
            author: author ? [author] : [],
            custodian,
            section: sectionEntries
        } as Composition;
    }

    private buildDocumentBundle(
        input: ReferralWorkflowInput,
        composition: Composition,
        grouped: GroupedSnapshotResources,
        referralIdentifier: string,
        authoredOn: string
    ): Bundle {
        const resources = this.uniqueResources([
            input.source.patient,
            ...(input.source.encounter ? [input.source.encounter] : []),
            ...(input.destination.organizationResource ? [input.destination.organizationResource] : []),
            ...grouped.allergies,
            ...grouped.vitals,
            ...grouped.physicalExam,
            ...grouped.patientSymptoms,
            ...grouped.labRequests,
            ...grouped.otherServiceRequests,
            ...grouped.diagnosticReports,
            ...grouped.medicationRequests,
            ...grouped.diagnoses,
            ...grouped.pastDiagnoses,
            ...grouped.immunizations,
            ...grouped.additionalResources
        ]);

        const entries: BundleEntry[] = [
            { fullUrl: this.referenceValue(composition), resource: composition as BundleEntry['resource'] },
            ...resources.map(resource => ({
                fullUrl: this.referenceValue(resource),
                resource: resource as BundleEntry['resource']
            }))
        ];

        return {
            resourceType: 'Bundle',
            type: 'document',
            identifier: {
                system: this.referralIdentifierSystem,
                value: referralIdentifier
            },
            timestamp: authoredOn,
            entry: entries
        } as Bundle;
    }

    private buildDocumentReference(
        input: ReferralWorkflowInput,
        documentBundle: Bundle,
        referralIdentifier: string,
        authoredOn: string,
        title: string
    ): DocumentReference {
        const author = this.buildAuthorReference(input);
        const bundlePayload = this.encodeJson(documentBundle);

        return {
            resourceType: 'DocumentReference',
            status: 'current',
            docStatus: 'final',
            identifier: [
                {
                    system: this.referralIdentifierSystem,
                    value: referralIdentifier
                }
            ],
            type: this.buildCodeableConcept('referral-document', 'Referral Document'),
            category: [this.buildCodeableConcept('clinical-document', 'Clinical Document')],
            subject: this.toReference(input.source.patient),
            date: authoredOn,
            author: author ? [author] : [],
            description: `${title} for ${this.patientDisplayName(input.source.patient)}`,
            content: [
                {
                    attachment: {
                        contentType: 'application/fhir+json',
                        title: `${title}.json`,
                        data: bundlePayload,
                        creation: authoredOn
                    }
                }
            ],
            context: input.source.encounter
                ? {
                    encounter: [this.toReference(input.source.encounter)]
                }
                : undefined
        } as DocumentReference;
    }

    private buildSections(grouped: GroupedSnapshotResources): Composition['section'] {
        const sections = [
            this.buildSection('Allergies', grouped.allergies),
            this.buildSection('Vitals', grouped.vitals),
            this.buildSection('Physical Exam', grouped.physicalExam),
            this.buildSection('Patient Reported Symptoms & Social History', grouped.patientSymptoms),
            this.buildNarrativeSection('Referral Notes', grouped.referralNotes),
            this.buildSection('Lab Service Requests', grouped.labRequests),
            this.buildSection('Other Service Requests', grouped.otherServiceRequests),
            this.buildSection('Diagnostic Reports', grouped.diagnosticReports),
            this.buildSection('Medications', grouped.medicationRequests),
            this.buildSection('Current Diagnoses', grouped.diagnoses),
            this.buildSection('Past Diagnoses', grouped.pastDiagnoses),
            this.buildSection('Immunizations', grouped.immunizations)
        ].filter((section): section is NonNullable<Composition['section']>[number] => !!section);

        return sections.length ? sections : undefined;
    }

    private buildSection(title: string, resources: Resource[]): NonNullable<Composition['section']>[number] | undefined {
        const references = resources
            .map(resource => this.toReference(resource))
            .filter((reference): reference is Reference => !!reference.reference);

        if (!references.length) {
            return undefined;
        }

        return {
            title,
            entry: references
        };
    }

    private buildNarrativeSection(title: string, noteText?: string): NonNullable<Composition['section']>[number] | undefined {
        const normalized = (noteText ?? '').trim();
        if (!normalized) {
            return undefined;
        }

        return {
            title,
            text: {
                status: 'generated',
                div: `<div>${this.escapeHtml(normalized).replace(/\n/g, '<br/>')}</div>`
            }
        };
    }

    private groupResources(input: ReferralWorkflowInput): GroupedSnapshotResources {
        const patientReference = this.referenceValue(input.source.patient);
        const encounterReference = this.referenceValue(input.source.encounter);
        const observations = this.uniqueResources(input.source.observations) as Observation[];
        const conditions = this.uniqueResources(input.source.conditions) as Condition[];
        const serviceRequests = this.uniqueResources(input.source.serviceRequests) as ServiceRequest[];

        return {
            allergies: this.uniqueResources(input.source.allergies) as AllergyIntolerance[],
            vitals: observations.filter(obs => this.matchesObservationCategory(obs, 'vital-signs', patientReference, encounterReference)),
            physicalExam: observations.filter(obs => this.isPhysicalExamObservation(obs, patientReference, encounterReference)),
            patientSymptoms: observations.filter(obs => this.isPatientSymptomObservation(obs, patientReference, encounterReference)),
            referralNotes: input.note?.trim() || undefined,
            labRequests: serviceRequests.filter(request => this.isLabServiceRequest(request)),
            otherServiceRequests: serviceRequests.filter(request => !this.isLabServiceRequest(request)),
            diagnosticReports: this.uniqueResources(input.source.diagnosticReports) as DiagnosticReport[],
            medicationRequests: this.uniqueResources(input.source.medicationRequests) as MedicationRequest[],
            diagnoses: conditions.filter(condition => this.isCurrentDiagnosis(condition, patientReference, encounterReference)),
            pastDiagnoses: conditions.filter(condition => this.isPastDiagnosis(condition, patientReference, encounterReference)),
            immunizations: this.uniqueResources(input.source.immunizations) as Immunization[],
            additionalResources: this.uniqueResources(input.source.additionalResources)
        };
    }

    private matchesObservationCategory(
        observation: Observation,
        categoryCode: string,
        patientReference?: string,
        encounterReference?: string
    ): boolean {
        const categories = Array.isArray(observation.category) ? observation.category : [];
        const normalizedCategory = categoryCode.toLowerCase();
        const hasCategory = categories.some(category =>
            (category.coding ?? []).some(coding => (coding.code ?? '').toLowerCase() === normalizedCategory)
            || (category.text ?? '').toLowerCase().includes(normalizedCategory)
        );

        if (!hasCategory) {
            return false;
        }

        if (patientReference && observation.subject?.reference && observation.subject.reference !== patientReference) {
            return false;
        }

        if (encounterReference && observation.encounter?.reference && observation.encounter.reference !== encounterReference) {
            return false;
        }

        return true;
    }

    private isPhysicalExamObservation(
        observation: Observation,
        patientReference?: string,
        encounterReference?: string
    ): boolean {
        if (!this.matchesObservationCategory(observation, 'exam', patientReference, encounterReference)) {
            return false;
        }

        if (!this.isFinalOrCorrectedObservation(observation)) {
            return false;
        }

        if (this.hasObservationCategory(observation, 'patient-symptoms')) {
            return false;
        }

        return !this.isPatientPerformedObservation(observation, patientReference);
    }

    private isPatientSymptomObservation(
        observation: Observation,
        patientReference?: string,
        encounterReference?: string
    ): boolean {
        const hasPatientSymptomsMarker = this.matchesObservationCategory(observation, 'patient-symptoms', patientReference, encounterReference);
        const isLegacyBaseCategory = this.matchesObservationCategory(observation, 'exam', patientReference, encounterReference)
            || this.matchesObservationCategory(observation, 'social-history', patientReference, encounterReference);

        if (!hasPatientSymptomsMarker && !isLegacyBaseCategory) {
            return false;
        }

        if (!this.isFinalOrCorrectedObservation(observation)) {
            return false;
        }

        if (hasPatientSymptomsMarker) {
            return true;
        }

        return this.isPatientPerformedObservation(observation, patientReference);
    }

    private hasObservationCategory(observation: Observation, categoryCode: string): boolean {
        return (observation.category ?? []).some(category =>
            (category.coding ?? []).some(coding => (coding.code ?? '').toLowerCase() === categoryCode)
            || (category.text ?? '').toLowerCase().includes(categoryCode)
        );
    }

    private isFinalOrCorrectedObservation(observation: Observation): boolean {
        const status = `${observation.status ?? ''}`.toLowerCase();
        return status === 'final' || status === 'corrected';
    }

    private isPatientPerformedObservation(observation: Observation, patientReference?: string): boolean {
        const expectedPatientReferences = new Set(
            [patientReference, this.normalizeReference(patientReference)]
                .filter(Boolean) as string[]
        );

        return (observation.performer ?? []).some(performer => {
            const reference = performer.reference ?? '';
            if (!reference) {
                return false;
            }

            if (expectedPatientReferences.has(reference) || expectedPatientReferences.has(this.normalizeReference(reference))) {
                return true;
            }

            return reference.toLowerCase().includes('patient/');
        });
    }

    private normalizeReference(reference?: string): string {
        if (!reference) {
            return '';
        }

        const clean = reference.split('?')[0];
        const parts = clean.split('/').filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
        }

        return clean;
    }

    private isLabServiceRequest(request: ServiceRequest): boolean {
        return (request.category ?? []).some(category => {
            const text = `${category.text ?? ''} ${(category.coding ?? []).map(coding => `${coding.code ?? ''} ${coding.display ?? ''}`).join(' ')}`.toLowerCase();
            return text.includes('lab') || text.includes('laboratory');
        });
    }

    private isCurrentDiagnosis(condition: Condition, patientReference?: string, encounterReference?: string): boolean {
        if (patientReference && condition.subject?.reference && condition.subject.reference !== patientReference) {
            return false;
        }

        // Must have an encounter reference and it must match the current one
        const encounterRef = (condition as any).encounter?.reference;
        if (!encounterReference || !encounterRef || encounterRef !== encounterReference) {
            return false;
        }

        const verification = `${condition.verificationStatus?.coding?.[0]?.code ?? condition.verificationStatus?.text ?? ''}`.toLowerCase();
        const clinical = `${condition.clinicalStatus?.coding?.[0]?.code ?? condition.clinicalStatus?.text ?? ''}`.toLowerCase();

        // Must be an active/confirmed finding
        if (['resolved', 'inactive', 'entered-in-error'].includes(clinical)) return false;
        if (['refuted', 'entered-in-error'].includes(verification)) return false;

        return ['confirmed', 'differential', 'provisional'].includes(verification) || clinical === 'active';
    }

    private isPastDiagnosis(condition: Condition, patientReference?: string, encounterReference?: string): boolean {
        if (patientReference && condition.subject?.reference && condition.subject.reference !== patientReference) {
            return false;
        }

        // If it's already a current diagnosis, it shouldn't be in history
        if (this.isCurrentDiagnosis(condition, patientReference, encounterReference)) {
            return false;
        }

        // Anything else for this patient is technically history (resolved, inactive, or active from previous encounter)
        const clinical = `${condition.clinicalStatus?.coding?.[0]?.code ?? condition.clinicalStatus?.text ?? ''}`.toLowerCase();
        if (clinical === 'entered-in-error') return false;

        return true;
    }

    private uniqueResources<T extends Resource>(resources: T[] | undefined | null): T[] {
        const seen = new Set<string>();
        const output: T[] = [];

        for (const resource of resources ?? []) {
            if (!resource?.resourceType) {
                continue;
            }

            const key = this.referenceValue(resource) || `${resource.resourceType}:${JSON.stringify(resource)}`;
            if (seen.has(key)) {
                continue;
            }

            seen.add(key);
            output.push(resource);
        }

        return output;
    }

    private buildAuthorReference(input: ReferralWorkflowInput): Reference | undefined {
        if (input.requesterReference?.trim()) {
            return {
                reference: input.requesterReference.trim(),
                display: input.requesterDisplay?.trim() || undefined
            };
        }

        return this.buildOrganizationReference(input.sourceOrganizationId, input.sourceOrganizationDisplay);
    }

    private buildOrganizationReference(organizationId?: string, organizationDisplay?: string): Reference | undefined {
        const normalizedId = organizationId?.trim();
        if (!normalizedId) {
            return undefined;
        }

        return {
            reference: `Organization/${normalizedId}`,
            display: organizationDisplay?.trim() || undefined
        };
    }

    private toReference(resource: Resource | undefined): Reference {
        const reference = this.referenceValue(resource);
        return {
            reference,
            display: this.resourceDisplay(resource)
        };
    }

    private referenceValue(resource: Resource | undefined): string {
        if (!resource?.resourceType || !resource.id) {
            return '';
        }

        return `${resource.resourceType}/${resource.id}`;
    }

    private resourceDisplay(resource: Resource | undefined): string | undefined {
        if (!resource) {
            return undefined;
        }

        if (resource.resourceType === 'Patient') {
            return this.patientDisplayName(resource as Patient);
        }

        if (resource.resourceType === 'Organization') {
            return (resource as Organization).name || undefined;
        }

        return (resource as any).code?.text
            || (resource as any).code?.coding?.[0]?.display
            || (resource as any).title
            || (resource as any).description
            || resource.id
            || undefined;
    }

    private patientDisplayName(patient: Patient): string {
        const name = patient.name?.[0];
        const given = name?.given?.join(' ') ?? '';
        const family = name?.family ?? '';
        return [given, family].filter(Boolean).join(' ').trim() || patient.id || 'Patient';
    }

    private buildCodeableConcept(code: string, display: string): CodeableConcept {
        const coding: Coding = {
            system: this.referralWorkflowSystem,
            code,
            display
        };

        return {
            coding: [coding],
            text: display
        };
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private encodeJson(value: unknown): string {
        const json = JSON.stringify(value, null, 2);
        const bytes = new TextEncoder().encode(json);
        let binary = '';

        bytes.forEach(byte => {
            binary += String.fromCharCode(byte);
        });

        return btoa(binary);
    }

    private createIdentifierValue(): string {
        return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `ref-${Date.now()}`;
    }
}